import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { TicketDto, TicketKey } from 'api/Tickets/Types/TicketDto'
import { HeaderSortCell } from 'components/Filter'
import { Link } from 'components/Link'
import { formatLocalDateUTC, useDifferenceInFullDays } from 'utils/date'
import { isEnabledOnEnvs, links, ticketDetailsRouteUrl } from 'utils/featureToggles'

import { appENV } from '../../utils/dom'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

export const ticketsTranslationColumn: GridColumn[] = [
  {
    title: 'Number',
    field: TicketKey.NUMBER,
    cell: (props: GridCellProps) => (
      <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
        <Link
          to={{
            pathname: ticketDetailsRouteUrl(props.dataItem.id, props.dataItem.viewType.id),
          }}
        >
          {props.dataItem.number}
        </Link>
      </td>
    ),
    show: true,
    filterable: true,
    sortable: false,
    width: 95,
    filter: 'text',
  },
  {
    title: 'Type',
    field: TicketKey.TYPE,
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Client',
    field: TicketKey.CLIENT,
    cell: (props: GridCellProps): JSX.Element => {
      if (!props.dataItem.client) {
        return <td />
      }
      return (
        <td
          className='table-columns__content'
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsClients.detailsUrl as string, {
                id: props.dataItem.client.id,
                slug: 'details',
              }),
            }}
          >
            {props.dataItem.client.name}
          </Link>
        </td>
      )
    },
    show: true,
    filterable: true,
    sortable: false,
    filter: 'text',
  },
  {
    title: 'Country',
    field: TicketKey.COUNTRY,
    cell: (props: GridCellProps): JSX.Element => {
      const isSalesTicketType = props.dataItem.viewType.id === 3
      const data: TicketDto = props.dataItem
      if (isSalesTicketType) {
        if (data.client) {
          return <td>{data.client?.country?.name}</td>
        }
        if (!data.lead?.country.id) {
          return <td />
        }
        return <td>{data.lead?.country.name}</td>
      }
      if (!data.client?.country?.id) {
        return <td />
      }
      return <td>{props.dataItem.client.country.name}</td>
    },
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Status',
    field: TicketKey.STATUS,
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Assignee',
    field: TicketKey.ASSIGNEE,
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Reporter',
    field: TicketKey.REPORTER,
    show: true,
    filterable: true,
    sortable: false,
    className: 'table-columns__content',
    filter: 'text',
  },
  {
    title: 'Entity',
    field: TicketKey.TICKMILL_COMPANY,
    cell: (props: GridCellProps): JSX.Element => {
      const isSalesTicketType = props.dataItem.viewType.id === 3
      if (isSalesTicketType) {
        if (!props.dataItem?.lead?.tickmillCompany?.id) {
          return <td />
        }
        return (
          <td>
            <CompanyTag id={props.dataItem.lead.tickmillCompany.id}>
              {props.dataItem.lead.tickmillCompany.name}
            </CompanyTag>
          </td>
        )
      }
      if (!props.dataItem?.client?.tickmillCompany?.id) {
        return <td />
      }
      return (
        <td>
          <CompanyTag id={props.dataItem.client.tickmillCompany.id}>
            {props.dataItem.client.tickmillCompany.name}
          </CompanyTag>
        </td>
      )
    },
    show: true,
    sortable: false,
    filter: 'text',
    width: 80,
    minResizableWidth: 80,
  },
  {
    title: 'Date Created',
    field: TicketKey.DATE_CREATED,
    headerCell: HeaderSortCell,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>
        {formatLocalDateUTC(props.dataItem.createDate, true)}
        <br />
        {useDifferenceInFullDays(props.dataItem.createDate)}
      </td>
    ),
    show: true,
    filterable: true,
    sortable: true,
    width: 150,
    filter: 'text',
  },
  {
    title: 'Date Updated',
    field: TicketKey.DATE_UPDATED,
    headerCell: HeaderSortCell,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>
        {props.dataItem.updateDate && (
          <>
            {formatLocalDateUTC(props.dataItem.updateDate, true)}
            <br />
            {useDifferenceInFullDays(props.dataItem.updateDate)}
          </>
        )}
      </td>
    ),
    show: isEnabledOnEnvs([appENV.development, appENV.localhost]),
    filterable: true,
    sortable: true,
    width: 150,
    filter: 'text',
  },
]
